import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { forkJoin } from "rxjs";
import { Agent, Organization, OrganizationClient, ServiceRequest, ServiceRequestClient, ShippingMethod, ShippingMethodClient } from "src/app/web-api-client";
import { WtStorageService } from "src/app/wt-storage.service";
import { OrganizationalTypes } from "../../enums/organizational-types.enum";
import { CreateTaxSettlementComponent } from "../create-tax-settlement/create-tax-settlement.component";
import { ServiceRequestStatusHelper } from "../../shared/enum/serviceRequestStatus.enum";
import { PaymentStatusHelper } from "../../shared/enum/paymentStatus.enum";
import { ServiceRequestResponseTypeUtil } from "src/app/shared/utils/service-request-response.utils";
import { AllServiceRequestsService } from "../../services/all-service-requests.service";

// Filter interface for managing filter state
interface ServiceRequestFilters {
  // Default filters (always visible)
  requestor: string;
  egn: string;
  uri: string;
  rnu: string;
  serviceName: string;

  // Extended filters (hidden by default)
  responseType: number | null;
  serviceType: string;
  providerTaxMin: number | null;
  providerTaxMax: number | null;
  shippingMethod: number | null;
  status: number | null;
  fromDate: Date | null;
  toDate: Date | null;
}

// Column configuration interface
interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  order: number;
}

@Component({
  selector: 'app-all-service-requests',
  templateUrl: './all-service-requests.component.html',
  styleUrls: ['./all-service-requests.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AllServiceRequestsComponent implements OnInit {

  public isLoaded: boolean = false;
  selectedService: ServiceRequest;
  public services: ServiceRequest[];
  public filteredServices: ServiceRequest[];
  public currentAgent: Agent;
  public orgs: Organization[] = [];
  public shippingMethods: ShippingMethod[] = [];

  // Toggle for extended filters
  public showExtendedFilters: boolean = false;

  // Toggle for column configuration panel
  public showColumnConfig: boolean = false;

  // Column configuration
  public columns: ColumnConfig[] = [
    { key: 'requestor', label: 'Заявител', visible: true, order: 1 },
    { key: 'egn', label: 'ЕГН', visible: true, order: 2 },
    { key: 'responseType', label: 'Отговор', visible: false, order: 3 },
    { key: 'serviceName', label: 'Вид услуга', visible: false, order: 4 },
    { key: 'serviceType', label: 'Услуга', visible: true, order: 5 },
    { key: 'uri', label: 'УРИ', visible: true, order: 6 },
    { key: 'rnu', label: 'RNU', visible: false, order: 7 },
    { key: 'provider', label: 'Доставчик', visible: false, order: 8 },
    { key: 'providerTax', label: 'Доставчик такса', visible: true, order: 9 },
    { key: 'agent', label: 'Посредник', visible: false, order: 10 },
    { key: 'shippingMethod', label: 'Метод за Доставка', visible: false, order: 11 },
    { key: 'deliveryDate', label: 'Дата на Доставка', visible: false, order: 12 },
    { key: 'status', label: 'Статус Заявка', visible: true, order: 13 },
    { key: 'paymentStatus', label: 'Статус Плащане', visible: true, order: 14 },
    { key: 'requestDate', label: 'Дата на Заявяване', visible: true, order: 15 },
    { key: 'organization', label: 'Организация', visible: false, order: 16 }
  ];

  // Filter properties
  public filters: ServiceRequestFilters = {
    // Default filters
    requestor: '',
    egn: '',
    uri: '',
    rnu: '',
    serviceName: '',

    // Extended filters
    responseType: null,
    serviceType: '',
    providerTaxMin: null,
    providerTaxMax: null,
    shippingMethod: null,
    status: null,
    fromDate: null,
    toDate: null
  };

  // Available options for dropdowns
  public responseTypes = [
    { value: 0, label: 'Не е Избран' },
    { value: 1, label: 'Телефон' },
    { value: 2, label: 'Имейл' },
    { value: 3, label: 'Гише' }
  ];

  public statuses = [
    { value: 0, label: 'Нова' },
    { value: 1, label: 'В Процес' },
    { value: 2, label: 'Завършена' },
    { value: 3, label: 'Грешка' },
    { value: -1, label: 'Чернова' },
    { value: 4, label: 'Автоматизирана Нова' },
    { value: 5, label: 'Автоматизирана Платена' },
    { value: 6, label: 'Автоматизирано Плащане Изтекло' },
    { value: 7, label: 'Автоматизирано Плащане Спряно' },
    { value: 8, label: 'Автоматизирано Плащане в Процес' },
    { value: 9, label: 'Автоматизирано Плащане Регистрирано' }
  ];

  constructor(
    private readonly changeDetectionRef: ChangeDetectorRef,
    private readonly modalService: NgbModal,
    public orgClient: OrganizationClient,
    public localStore: WtStorageService,
    public client: ServiceRequestClient,
    public allServiceRequestsService: AllServiceRequestsService,
    public shippingMethodClient: ShippingMethodClient
  ) {
    this.currentAgent = localStore.getАgent();
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData() {
    this.isLoaded = false;
    forkJoin([
      this.allServiceRequestsService.getAllServiceRequests(),
      this.orgClient.getList(),
      this.shippingMethodClient.getList()
    ])
      .subscribe(([allServices, allOrgs, shippingMethods]) => {
        this.orgs = allOrgs;
        this.services = allServices;
        this.filteredServices = allServices;
        this.shippingMethods = shippingMethods.filter(m => m.isActive);
        this.isLoaded = true;
        this.changeDetectionRef.detectChanges();
      });
  }

  public onSelected(service: ServiceRequest) {
    if (this.isSelected(service)) {
      this.selectedService = null;
    }
    else {
      this.selectedService = service;
    }
  }

  public isSelected(service: ServiceRequest): boolean {
    return this.selectedService == service;
  }

  public getAgentName(serviceRequest: ServiceRequest) {
    return serviceRequest.agentService ? serviceRequest.agentService.agent.agentName : this.currentAgent.agentName;
  }

  public onCreation(): void {
    // Implementation for creation if needed
  }

  public onDelete(): void {
    // Implementation for deletion if needed
  }

  public onEdit(): void {
    // Implementation for editing if needed
  }

  public getRequestorNames(serviceRequest: ServiceRequest) {
    return `${serviceRequest.firstName} ${serviceRequest.fammily}`
  }

  public isSumTaxSettlement(serviceRequest: ServiceRequest) {
    return serviceRequest.isSettlementGenerated ? 'Да' : 'Не';
  }

  isGlobalOrg() {
    return this.localStore.getuser().organization.organizationalType == OrganizationalTypes.Global;
  }

  getGlobalOrg(service: ServiceRequest) {
    let org = this.orgs.find(d => d.id == service.organizationId);
    return org ? org.title : 'Няма Организация';
  }

  getServiceName(service: ServiceRequest) {
    return service.deliverService.service.serviceName;
  }

  getDate(service: ServiceRequest): string {
    const date = new Date(service.dateReg);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() returns 0-11
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  public getTranslatedStatus(status: number): string {
    return ServiceRequestStatusHelper.GetCyrilicName(status);
  }

  public getPaymentStatusText(service: ServiceRequest): string {
    const payments = service.payments;
    if (!payments || payments.length === 0) return PaymentStatusHelper.GetCyrilicName(null);

    const payment = payments[0];
    return PaymentStatusHelper.GetCyrilicName(payment.meyPaymentStatus);
  }

  public getPaymentBadgeClass(service: ServiceRequest): string {
    const payments = service.payments;
    if (!payments || payments.length === 0) return PaymentStatusHelper.GetBadgeClass(null);

    const payment = payments[0];
    return PaymentStatusHelper.GetBadgeClass(payment.meyPaymentStatus);
  }

  getURI(service: ServiceRequest) {
    return service.deliverService.service.yri;
  }

  openTaxSettlementModal() {
    const modalRef = this.modalService.open(CreateTaxSettlementComponent, { size: 'xl', centered: true, backdrop: 'static' });
    modalRef.componentInstance.services = this.services; // Show all services, not filtered by organization
    modalRef.componentInstance.onClose.subscribe(r => {
      this.loadData();
    });

    modalRef.result.then((result) => {
      // Handle the result from the modal
    }, (reason) => {
      // Handle the dismissal of the modal
    });
  }

  public GetResponseType(serviceRequest: ServiceRequest) {
    return ServiceRequestResponseTypeUtil.getCyrilicLabel(serviceRequest.responseType);
  }

  // Additional method to get organization name for display
  public getOrganizationName(service: ServiceRequest): string {
    const org = this.orgs.find(d => d.id == service.organizationId);
    return org ? org.title : 'Неизвестна Организация';
  }

  getShippingMethodName(service: ServiceRequest) {
    return service.shippingMethod ? service.shippingMethod.name : '-';
  }

  getDeliveredDate(service: ServiceRequest) {
    return service.deliveredDate ? new Date(service.deliveredDate).toLocaleDateString('bg-BG') : '-';
  }

  /**
   * Truncates text to specified maximum length and adds ellipsis if truncated
   * @param text Text to truncate
   * @param maxLength Maximum length (default: 200)
   * @returns Truncated text with ellipsis if needed
   */
  truncateText(text: string, maxLength: number = 200): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  /**
   * Apply all active filters to the service requests
   */
  public applyFilters(): void {
    this.filteredServices = this.services.filter(service => {
      // DEFAULT FILTERS

      // Text filter: Requestor (First Name + Family Name)
      if (this.filters.requestor && this.filters.requestor.trim() !== '') {
        const requestorName = `${service.firstName} ${service.fammily}`.toLowerCase();
        if (!requestorName.includes(this.filters.requestor.toLowerCase())) {
          return false;
        }
      }

      // Text filter: EGN
      if (this.filters.egn && this.filters.egn.trim() !== '') {
        if (!service.egn || !service.egn.toLowerCase().includes(this.filters.egn.toLowerCase())) {
          return false;
        }
      }

      // Text filter: URI
      if (this.filters.uri && this.filters.uri.trim() !== '') {
        const uri = service.deliverService?.service?.yri?.toLowerCase() || '';
        if (!uri.includes(this.filters.uri.toLowerCase())) {
          return false;
        }
      }

      // Text filter: RNU
      if (this.filters.rnu && this.filters.rnu.trim() !== '') {
        if (!service.rnu || !service.rnu.toLowerCase().includes(this.filters.rnu.toLowerCase())) {
          return false;
        }
      }

      // Text filter: Service Name (Title)
      if (this.filters.serviceName && this.filters.serviceName.trim() !== '') {
        const serviceName = service.deliverService?.title?.toLowerCase() || '';
        if (!serviceName.includes(this.filters.serviceName.toLowerCase())) {
          return false;
        }
      }

      // EXTENDED FILTERS

      // Dropdown filter: Response Type
      if (this.filters.responseType !== null && this.filters.responseType !== undefined) {
        if (service.responseType !== this.filters.responseType) {
          return false;
        }
      }

      // Text filter: Service Type
      if (this.filters.serviceType && this.filters.serviceType.trim() !== '') {
        const serviceType = service.deliverService?.service?.serviceName?.toLowerCase() || '';
        if (!serviceType.includes(this.filters.serviceType.toLowerCase())) {
          return false;
        }
      }

      // Numeric filter: Provider Tax (Min)
      if (this.filters.providerTaxMin !== null && this.filters.providerTaxMin !== undefined) {
        if (service.serviceProviderTax < this.filters.providerTaxMin) {
          return false;
        }
      }

      // Numeric filter: Provider Tax (Max)
      if (this.filters.providerTaxMax !== null && this.filters.providerTaxMax !== undefined) {
        if (service.serviceProviderTax > this.filters.providerTaxMax) {
          return false;
        }
      }

      // Dropdown filter: Shipping Method
      if (this.filters.shippingMethod !== null && this.filters.shippingMethod !== undefined) {
        if (service.idShippingMethod !== this.filters.shippingMethod) {
          return false;
        }
      }

      // Dropdown filter: Status
      if (this.filters.status !== null && this.filters.status !== undefined) {
        if (service.status !== this.filters.status) {
          return false;
        }
      }

      // Date filter: From Date
      if (this.filters.fromDate) {
        const serviceDate = new Date(service.dateReg);
        const fromDate = new Date(this.filters.fromDate);
        fromDate.setHours(0, 0, 0, 0);
        if (serviceDate < fromDate) {
          return false;
        }
      }

      // Date filter: To Date
      if (this.filters.toDate) {
        const serviceDate = new Date(service.dateReg);
        const toDate = new Date(this.filters.toDate);
        toDate.setHours(23, 59, 59, 999);
        if (serviceDate > toDate) {
          return false;
        }
      }

      return true;
    });

    this.changeDetectionRef.detectChanges();
  }

  /**
   * Clear all filters and reset to show all service requests
   */
  public clearAllFilters(): void {
    this.filters = {
      // Default filters
      requestor: '',
      egn: '',
      uri: '',
      rnu: '',
      serviceName: '',

      // Extended filters
      responseType: null,
      serviceType: '',
      providerTaxMin: null,
      providerTaxMax: null,
      shippingMethod: null,
      status: null,
      fromDate: null,
      toDate: null
    };
    this.filteredServices = this.services;
    this.changeDetectionRef.detectChanges();
  }

  /**
   * Toggle extended filters visibility
   */
  public toggleExtendedFilters(): void {
    this.showExtendedFilters = !this.showExtendedFilters;
    this.changeDetectionRef.detectChanges();
  }

  /**
   * Toggle column configuration panel visibility
   */
  public toggleColumnConfig(): void {
    this.showColumnConfig = !this.showColumnConfig;
    this.changeDetectionRef.detectChanges();
  }

  /**
   * Get visible columns sorted by order
   */
  public getVisibleColumns(): ColumnConfig[] {
    return this.columns.filter(col => col.visible).sort((a, b) => a.order - b.order);
  }

  /**
   * Check if a column is visible
   */
  public isColumnVisible(key: string): boolean {
    const column = this.columns.find(col => col.key === key);
    return column ? column.visible : false;
  }

  /**
   * Toggle column visibility
   */
  public toggleColumn(key: string): void {
    const column = this.columns.find(col => col.key === key);
    if (column) {
      column.visible = !column.visible;
      this.changeDetectionRef.detectChanges();
    }
  }

  /**
   * Reset columns to default visibility
   */
  public resetColumnsToDefault(): void {
    this.columns.forEach(col => {
      // Default visible columns: Заявител, ЕГН, Тип, УРИ, Доставчик такса, Статус Заявка, Статус Плащане, Дата на Заявяване
      col.visible = ['requestor', 'egn', 'serviceType', 'uri', 'providerTax', 'status', 'paymentStatus', 'requestDate'].includes(col.key);
    });
    this.changeDetectionRef.detectChanges();
  }

  /**
   * Show all columns
   */
  public showAllColumns(): void {
    this.columns.forEach(col => col.visible = true);
    this.changeDetectionRef.detectChanges();
  }

  /**
   * Hide all columns except default ones
   */
  public hideAllColumns(): void {
    this.columns.forEach(col => col.visible = false);
    this.changeDetectionRef.detectChanges();
  }
}
